/**
 * Utility functions for Measurements page
 */

import type {
  Meranie,
  ChartDataPoint,
  MeasurementSummary,
  Klient,
} from "./types";

/**
 * Sort measurements by date (newest first)
 */
export function sortMeasurementsByDate(measurements: Meranie[]): Meranie[] {
  return [...measurements].sort((a, b) => {
    return new Date(b.datum).getTime() - new Date(a.datum).getTime();
  });
}

/**
 * Prepare chart data from measurements (oldest to newest for chart display)
 */
export function prepareChartData(measurements: Meranie[]): ChartDataPoint[] {
  return measurements
    .slice()
    .reverse()
    .map(m => ({
      date: new Date(m.datum).toLocaleDateString(),
      weight: parseFloat(m.vahaKg || "0"),
      fat: parseFloat(m.tukPercent || "0"),
      muscle: parseFloat(m.svalovinaPercent || "0"),
    }));
}

/**
 * Calculate fat mass in kg
 */
export function calculateFatMassKg(
  weight: string | null,
  fatPercent: string | null
): number {
  if (!weight || !fatPercent) return 0;
  return (parseFloat(weight) * parseFloat(fatPercent)) / 100;
}

/**
 * Calculate muscle mass in kg
 */
export function calculateMuscleMassKg(
  weight: string | null,
  musclePercent: string | null
): number {
  if (!weight || !musclePercent) return 0;
  return (parseFloat(weight) * parseFloat(musclePercent)) / 100;
}

/**
 * Prepare summary data from latest measurement
 */
export function prepareSummary(
  latestMeranie: Meranie | null
): MeasurementSummary | null {
  if (!latestMeranie) return null;

  return {
    weight: latestMeranie.vahaKg,
    bodyFat: latestMeranie.tukPercent,
    muscleMass: latestMeranie.svalovinaPercent,
    visceralFat: latestMeranie.visceralnytuk,
    date: latestMeranie.datum,
    fatMassKg: calculateFatMassKg(
      latestMeranie.vahaKg,
      latestMeranie.tukPercent
    ),
    muscleMassKg: calculateMuscleMassKg(
      latestMeranie.vahaKg,
      latestMeranie.svalovinaPercent
    ),
  };
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString();
}

/**
 * Format number with decimal places
 */
export function formatNumber(value: number, decimals: number = 1): string {
  return value.toFixed(decimals);
}

/**
 * Get client display name
 */
export function getClientDisplayName(klient: Klient): string {
  const fullName = [klient.meno, klient.priezvisko].filter(Boolean).join(" ");
  return fullName ? `${klient.alias} - ${fullName}` : klient.alias;
}
